import { isSafari } from 'react-device-detect'

interface Options {
  /**
   * The version of the browser to compare against.
   */
  version: number
  /**
   * The comparison operator to use.
   */
  comparisonOperator: 'greater' | 'lower' | 'equal'
}

/**
 * Checks if the user browser version is compatible with the given version. It returns true only if the browser is Safari and the version comparison is positive.
 *
 * Note: if you only need to check if the browser is Safari and you don't care about a specific version, use the `isSafari` variable from `react-device-detect` instead.
 *
 * @param userAgent - The user agent string that identifies the current browser.
 */
export const hasCompatibleSafariVersion = (
  userAgent: string,
  options: Options,
): boolean => {
  const detectedBrowserVersions = userAgent.match(/Version\/([^.]+)/)

  if (isSafari) {
    if (!detectedBrowserVersions) {
      // Path 1a: the browser is Safari but we couldn't detect the version. Since we cannot compare the requested version with the detected one, we stop here.
      console.warn("Couldn't detect the version in user agent.")

      return false
    }

    const detectedBrowserVersion = Number(detectedBrowserVersions[1])

    // Path 1b: the browser is Safari and we detected the version. We can compare the requested version with the detected one.
    switch (options.comparisonOperator) {
      case 'greater': {
        return detectedBrowserVersion > options.version
      }
      case 'lower': {
        return detectedBrowserVersion < options.version
      }
      case 'equal': {
        return detectedBrowserVersion === options.version
      }
      default: {
        return false
      }
    }
  }

  // Path 2: the browser is not Safari. The comparison cannot be made, so we fallback to false.
  return false
}
